import { useState } from "react";
import { ArrowUpRight, Check, AlertCircle, Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { api } from "@/lib/api";

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "", country: "",
    service: "", budget: "", message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      await api.post("/leads", { ...form, locale: lang });
      setStatus("success");
      setForm({ name: "", email: "", company: "", phone: "", country: "", service: "", budget: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail?.toString?.() || t.contact.error);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#0B132B] text-white relative overflow-hidden" data-testid="contact-section">
      <div className="absolute inset-0 bbm-grid-bg-dark opacity-50 pointer-events-none" />
      <div className="bbm-halo bg-[#0047FF] w-[500px] h-[500px] top-[20%] start-[-200px]" />
      <div className="bbm-halo bg-[#FFB800] w-[300px] h-[300px] bottom-[-100px] end-[10%] opacity-25" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="bbm-eyebrow !text-[#FFB800] mb-4">— {t.contact.eyebrow}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-6">
            {t.contact.title}
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-10">{t.contact.sub}</p>

          <div className="space-y-5 text-sm">
            {/* US HQ */}
            <div className="border border-white/10 rounded-lg p-4 bg-white/5 backdrop-blur" data-testid="contact-us-office">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-[#FFB800]">US Headquarters</span>
              </div>
              <div className="space-y-2 text-white/90">
                <a href="tel:+16107140171" className="flex items-center gap-2 hover:text-[#FFB800] transition-colors" data-testid="us-phone">
                  <Phone className="w-3.5 h-3.5 shrink-0" /> +1 610 714 0171
                </a>
                <div className="flex items-start gap-2 text-white/75">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>487 College Boulevard, Wenonah, NJ 08090, USA</span>
                </div>
              </div>
            </div>

            {/* Algeria Branch */}
            <div className="border border-white/10 rounded-lg p-4 bg-white/5 backdrop-blur" data-testid="contact-dz-office">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0047FF]" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF]">Algiers Office</span>
              </div>
              <div className="space-y-2 text-white/90">
                <a href="tel:+213668418716" className="flex items-center gap-2 hover:text-[#FFB800] transition-colors" data-testid="dz-phone">
                  <Phone className="w-3.5 h-3.5 shrink-0" /> +213 668 41 87 16
                </a>
                <div className="flex items-start gap-2 text-white/75">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>Villa 367, Sellier Hydra, Algiers, Algeria</span>
                </div>
              </div>
            </div>

            {/* Email + WhatsApp */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a href="mailto:Info@bus-management.com" className="flex items-center gap-3 border border-white/10 rounded-lg p-3 hover:border-[#FFB800] transition-colors" data-testid="contact-email-link">
                <Mail className="w-4 h-4 text-[#FFB800] shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">Email</div>
                  <div className="text-white text-xs truncate">Info@bus-management.com</div>
                </div>
              </a>
              <a
                href="https://wa.me/16107140171"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 border border-white/10 rounded-lg p-3 hover:border-[#25D366] transition-colors"
                data-testid="contact-whatsapp-link"
              >
                <MessageCircle className="w-4 h-4 text-[#25D366] shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/60">Tech Support · WhatsApp</div>
                  <div className="text-white text-xs">+1 610 714 0171</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white text-[#0B132B] rounded-2xl p-6 lg:p-10 border border-white/10"
          >
            {status === "success" ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div className="w-14 h-14 rounded-full bg-[#0047FF]/10 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-7 h-7 text-[#0047FF]" />
                </div>
                <h3 className="font-display text-2xl mb-2">{t.contact.success}</h3>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="bbm-btn-secondary mt-4"
                  data-testid="contact-reset-btn"
                >
                  ↻
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label={t.contact.name} required>
                    <input
                      type="text"
                      required
                      data-testid="contact-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder={t.contact.placeholders.name}
                      className="bbm-input"
                    />
                  </Field>
                  <Field label={t.contact.email} required>
                    <input
                      type="email"
                      required
                      data-testid="contact-email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder={t.contact.placeholders.email}
                      className="bbm-input"
                    />
                  </Field>
                  <Field label={t.contact.company}>
                    <input
                      type="text"
                      data-testid="contact-company"
                      value={form.company}
                      onChange={update("company")}
                      placeholder={t.contact.placeholders.company}
                      className="bbm-input"
                    />
                  </Field>
                  <Field label={t.contact.phone}>
                    <input
                      type="text"
                      data-testid="contact-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder={t.contact.placeholders.phone}
                      className="bbm-input"
                    />
                  </Field>
                  <Field label={t.contact.country}>
                    <input
                      type="text"
                      data-testid="contact-country"
                      value={form.country}
                      onChange={update("country")}
                      placeholder={t.contact.placeholders.country}
                      className="bbm-input"
                    />
                  </Field>
                  <Field label={t.contact.service}>
                    <select
                      data-testid="contact-service"
                      value={form.service}
                      onChange={update("service")}
                      className="bbm-input"
                    >
                      <option value="">—</option>
                      {t.contact.services_options.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t.contact.budget} full>
                    <select
                      data-testid="contact-budget"
                      value={form.budget}
                      onChange={update("budget")}
                      className="bbm-input"
                    >
                      <option value="">—</option>
                      {t.contact.budget_options.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t.contact.message} required full>
                    <textarea
                      required
                      rows={5}
                      data-testid="contact-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder={t.contact.placeholders.message}
                      className="bbm-input resize-none"
                    />
                  </Field>
                </div>

                {status === "error" && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-red-600" data-testid="contact-error">
                    <AlertCircle className="w-4 h-4" />
                    {error || t.contact.error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  data-testid="contact-submit"
                  className="bbm-btn-primary mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? t.contact.submitting : t.contact.submit}
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </>
            )}
          </form>
        </div>
      </div>

      <style>{`
        .bbm-input {
          width: 100%;
          padding: 0.75rem 0.875rem;
          border: 1px solid #E5E7EB;
          border-radius: 6px;
          background: #fff;
          font-size: 0.95rem;
          color: #0B132B;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .bbm-input:focus {
          outline: none;
          border-color: #0047FF;
          box-shadow: 0 0 0 3px rgba(0,71,255,0.12);
        }
      `}</style>
    </section>
  );
}

function Field({ label, required, full, children }) {
  return (
    <label className={`flex flex-col gap-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <span className="font-mono text-[11px] uppercase tracking-widest text-[#4B5563]">
        {label} {required && <span className="text-[#0047FF]">*</span>}
      </span>
      {children}
    </label>
  );
}
